import { useState } from 'react';
import {
  Building,
  Clock,
  Calendar,
  ChevronRight,
  ChevronDown,
  Users,
  Star,
  DollarSign,
  BarChart,
  TrendingUp,
  Check,
  FileText,
  Scale,
  Map,
  Award,
  Shield,
  AlertCircle
} from 'lucide-react';
import BidderCard from '../Bidder';
const Main = () => {
  const [bidStage, setBidStage] = useState('open');
  const [selectedFilters, setSelectedFilters] = useState({});

  // Mock data for qualified bidders
  const qualifiedBidders = [
    {
      name: "Global IP Partners",
      matchScore: 94,
      tasks: 234,
      premiumPartner: true,
      expertise: ["AI/ML", "Software Patents", "IP Strategy"],
      geography: ["UK", "EU", "US"],
      metrics: {
        speed: 92,
        quality: 95,
        value: 88,
        communication: 94,
        responseTime: "4 hours",
        successRate: "97%",
        returnRate: "92%"
      },
      team: {
        partner: "Sarah Chen",
        associates: 3,
        support: 2
      },
      specialization: {
        primary: "Technology",
        industries: ["Software", "AI", "Fintech"]
      },
      pricing: {
        range: "£8,000 - £12,000",
        model: "Fixed Fee",
        options: ["Phased Payments", "Success Bonus"]
      }
    },
  ];
  return (
    <div className="space-y-6">
      {/* Market Overview */}
      <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
        <div>
          <div className="flex items-center gap-2">
            <BarChart className="w-5 h-5" />
            <span className='font-bold'> Market Overview</span>
          </div>
        </div>
        <div>
          <div className="grid grid-cols-4 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="text-2xl font-bold">42</div>
              <div className="text-sm text-gray-500">Qualified Providers</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-2xl font-bold">£8.2k</div>
              <div className="text-sm text-gray-500">Average Bid</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-2xl font-bold">12</div>
              <div className="text-sm text-gray-500">Active Bids</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-2xl font-bold">92%</div>
              <div className="text-sm text-gray-500">Success Rate</div>
            </div>
          </div>
        </div>
      </div>

      <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
        <div>
          <div className="flex items-center gap-2">
            <Scale className="w-5 h-5" />
            <span className='font-bold'>Bid Management</span>
          </div>
        </div>
        <div>
          <div className="grid grid-cols-3 gap-4">
            {/* Time Controls */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Bid Duration</label>
              <select className="w-full focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary">
                <option value="">Select Duration</option>
                <option value="3">3 Days</option>
                <option value="5">5 Days</option>
                <option value="7">7 Days</option>
              </select>
            </div>

            {/* Provider Filters */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Provider Type</label>
              <select className="w-full focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary">
                <option value="">Select type</option>
                <option value="all">All Qualified</option>
                <option value="premium">Premium Only</option>
                <option value="specialists">Specialists</option>
              </select>
            </div>

            {/* Geographic Filters */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Geography</label>
              <select className="w-full focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary">
                <option value="">Select region</option>
                <option value="uk">UK Based</option>
                <option value="eu">EU Coverage</option>
                <option value="global">Global</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Qualified Bidders */}
      <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
        <div>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            <span className='font-bold'>Qualified Bidders</span>
          </div>
          <div>
            Ranked by match score and historical performance
          </div>
        </div>
        <div>
          <div className="space-y-4">
            {qualifiedBidders.map((firm, index) => (
              <BidderCard key={index} firm={firm} />
            ))}
          </div>
        </div>
      </div>

      {/* Insights & Analytics */}
      <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            <span className='font-bold'>Market Insights</span>
          </div>
        </div>
        <div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <h3 className="font-medium mb-3">Pricing Trends</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Average Bid</span>
                  <span>£8,200</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Price Range</span>
                  <span>£6,500 - £12,000</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Common Models</span>
                  <span>Fixed Fee (78%)</span>
                </div>
              </div>
            </div>

            <div className="p-4 border rounded-lg">
              <h3 className="font-medium mb-3">Timeline Analysis</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Average Duration</span>
                  <span>18 days</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Fastest 25%</span>
                  <span>12 days</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Response Time</span>
                  <span>4.2 hours</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default Main